import {
  earningsDateTip,
  earningsWithinExpiry,
  fmtEarningsDate,
  fmtEarningsInDays,
  fmtUsd,
  loadPositionsSnapshot,
  loadRecommendationsSnapshot,
  normalizeTicker,
  resolveShareAvgCostForTicker,
  sharesForTicker,
  type PositionsSnapshot,
  type WheelRecommendation,
} from "./watchlistData";
import { loadOwnLedgerStrikeAvgMap } from "./ownLedgerClient";
import { paintScreenerExpiryCell } from "./screenerExpiryCell";
import { paintScreenerSharesCell, parseSharesColumnAvailable } from "./screenerSharesCell";
import { isWebCompactMode } from "./webCompactMode";
import {
  callProfitability,
  fmtProfitPct,
  isCallSide,
  parseUsd,
  putProfitability,
} from "./profitability";
import { syncResetButtons } from "./filterReset";
import {
  applyCallsRiskyRowFilters,
  ensureCallsRiskyFilters,
} from "./callsScreenerRisky";

const PROFIT_HEADER = "sfe-profit-header";
const PROFIT_CELL = "sfe-profit-cell";
const PROFIT_COL = "sfe_profitability";
const EARNINGS_HEADER = "sfe-earnings-header";
const EARNINGS_CELL = "sfe-earnings-cell";
const EARNINGS_COL = "next_earnings_date";
const MIN_PROFIT_FILTER_ID = "sfe-put-min-profit-filter";
const MIN_PROFIT_HIDDEN = "sfe-min-profit-hidden";
const CALLS_SHARES_FILTER_ID = "sfe-calls-shares-filter";
const CALLS_SHARES_FILTER_HIDDEN = "sfe-calls-shares-filter-hidden";
const CALLS_SHARES_TIP =
  "Available shows tickers where you still have shares not tied to open short call contracts (Shares column avail > 0).";

let callsSharesFilter: "all" | "available" = "all";

const ANCHOR_COLS = ["pop", "roi", "last_price", "collateral", "holding_avg_cost"] as const;
/** Prefer Expiry so Earnings sits next to it (native tables lock widths via colgroup). */
const EARNINGS_ANCHOR_COLS = ["expiration_date", "expiration", "dte"] as const;

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let domObserver: MutationObserver | null = null;
let applying = false;
let minPutProfit: number | null = null;

function findPutsFilterRow(table: HTMLTableElement): HTMLElement | null {
  const rubric = document.getElementById("sfe-rubric-filter-host");
  if (rubric?.parentElement instanceof HTMLElement) return rubric.parentElement;

  const nativeRubric = document.getElementById("wheelPutRubricFilter");
  if (nativeRubric) {
    let field = nativeRubric.parentElement;
    while (field?.parentElement && field.parentElement !== document.body) {
      const parent = field.parentElement;
      if (parent.querySelectorAll("select, input, button").length >= 2) return parent;
      field = parent;
    }
  }

  const root =
    table.closest<HTMLElement>('[data-wheel-view="puts"]') ??
    table.parentElement?.parentElement;
  if (!root) return null;
  const labels = Array.from(root.querySelectorAll<HTMLElement>("label, span")).filter((el) =>
    /^(ticker|sector|expiration|expiry|rubric band|tradeable|tradable)$/i.test(
      (el.textContent ?? "").trim(),
    ),
  );
  for (const label of labels) {
    let row = label.parentElement;
    while (row && row !== root) {
      const count = labels.filter((item) => row?.contains(item)).length;
      if (count >= 2) return row;
      row = row.parentElement;
    }
  }
  return null;
}

function ensureMinProfitFilter(table: HTMLTableElement): void {
  if (tableSide(table) !== "put") return;
  let host = document.getElementById(MIN_PROFIT_FILTER_ID);
  if (!host) {
    host = document.createElement("label");
    host.id = MIN_PROFIT_FILTER_ID;
    host.className = "sfe-put-min-profit-filter";
    host.setAttribute("data-sfe-injected", "true");
    host.innerHTML = `
      <span class="sfe-put-min-profit-label">
        Min Profit.
        <span class="hint" tabindex="0" role="img" data-tip="Minimum premium yield on cash collateral: (last option price × 100) ÷ collateral.">i</span>
      </span>
      <span class="sfe-put-min-profit-control">
        <input type="number" step="0.1" inputmode="decimal" aria-label="Minimum profitability percent" />
        <span>%</span>
      </span>`;
    const input = host.querySelector<HTMLInputElement>("input");
    input?.addEventListener("input", () => {
      const value = input.value.trim();
      const parsed = Number(value);
      minPutProfit = value !== "" && Number.isFinite(parsed) ? parsed : null;
      scheduleSync();
    });
  }

  const input = host.querySelector<HTMLInputElement>("input");
  const expected = minPutProfit == null ? "" : String(minPutProfit);
  if (input && input.value !== expected && document.activeElement !== input) {
    input.value = expected;
  }

  const row = findPutsFilterRow(table);
  if (!row) return;
  const reset = Array.from(row.children).find((child) =>
    child.classList.contains("sfe-reset-filters"),
  );
  if (host.parentElement !== row || (reset && host.nextElementSibling !== reset)) {
    row.insertBefore(host, reset ?? null);
  }
}

const CALLS_FILTER_HIDDEN = "sfe-calls-screener-filter-hidden";
const CALLS_FILTER_CONTROL_IDS = [
  "wheelCallEarningsFilter",
  "wheelCallCapFilter",
  "wheelCallSectorFilter",
  "wheelCallRubricFilter",
] as const;

function hideCallsFilterField(controlId: string): void {
  const control = document.getElementById(controlId);
  if (!control) return;
  const field = control.closest(".filter-field");
  if (field instanceof HTMLElement) field.classList.add(CALLS_FILTER_HIDDEN);
}

function resetCallsHiddenFilters(root: HTMLElement): void {
  const earningsAll = root.querySelector<HTMLButtonElement>('[data-call-earnings="all"]');
  if (earningsAll && !earningsAll.classList.contains("active")) {
    earningsAll.click();
  }

  for (const id of ["wheelCallCapFilter", "wheelCallSectorFilter", "wheelCallRubricFilter"]) {
    const sel = document.getElementById(id);
    if (!(sel instanceof HTMLSelectElement)) continue;
    let changed = false;
    for (const opt of sel.options) {
      if (opt.selected) {
        opt.selected = false;
        changed = true;
      }
    }
    if (changed) {
      sel.dispatchEvent(new Event("input", { bubbles: true }));
      sel.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
}

function findCallsFilterRow(table: HTMLTableElement): HTMLElement | null {
  const ticker = document.getElementById("wheelCallTicker");
  const field = ticker?.closest(".filter-field");
  if (field?.parentElement instanceof HTMLElement) return field.parentElement;

  const root =
    table.closest<HTMLElement>('[data-wheel-view="calls"]') ??
    table.parentElement?.parentElement;
  if (!root) return null;

  const labels = Array.from(root.querySelectorAll<HTMLElement>("label, span")).filter((el) =>
    /^(quote|dte|ticker)$/i.test((el.textContent ?? "").trim()),
  );
  for (const label of labels) {
    let row = label.parentElement;
    while (row && row !== root) {
      const count = labels.filter((item) => row?.contains(item)).length;
      if (count >= 2) return row;
      row = row.parentElement;
    }
  }
  return null;
}

function rowSharesAvailable(
  row: HTMLTableRowElement,
  rec: WheelRecommendation | undefined,
  fromSnapshot: { available: number | null } | null,
): number | null {
  if (rec?.sfe_shares_available != null) return rec.sfe_shares_available;
  if (fromSnapshot?.available != null) return fromSnapshot.available;
  const cell =
    row.querySelector<HTMLTableCellElement>("td[data-col='open']") ??
    row.querySelector<HTMLTableCellElement>("td[data-col='held']");
  return parseSharesColumnAvailable(cell);
}

function applyCallsSharesFilter(
  row: HTMLTableRowElement,
  rec: WheelRecommendation | undefined,
  fromSnapshot: { available: number | null } | null,
): void {
  const available = rowSharesAvailable(row, rec, fromSnapshot);
  const visible =
    callsSharesFilter === "all" || (available != null ? available > 0 : false);
  row.classList.toggle(CALLS_SHARES_FILTER_HIDDEN, !visible);
}

function ensureSharesHeaderLabel(table: HTMLTableElement, colId: "held" | "open"): void {
  const th = table.querySelector<HTMLTableCellElement>(`thead th[data-col='${colId}']`);
  if (!th) return;
  const label = th.querySelector(".th-label");
  if (label) {
    label.textContent = "Shares";
    return;
  }
  if ((th.textContent ?? "").trim() === "Held") th.textContent = "Shares";
}

function ensureCallsSharesFilter(table: HTMLTableElement): void {
  if (tableSide(table) !== "call") return;

  let host = document.getElementById(CALLS_SHARES_FILTER_ID);
  if (!host) {
    host = document.createElement("div");
    host.id = CALLS_SHARES_FILTER_ID;
    host.className = "filter-field sfe-calls-shares-filter";
    host.setAttribute("data-sfe-injected", "true");
    host.innerHTML = `
      <label>Shares<span class="hint" tabindex="0" role="img" data-tip="${CALLS_SHARES_TIP}">i</span></label>
      <div class="year-chips" role="group" aria-label="Filter calls by share capacity">
        <button type="button" class="year-chip active" data-calls-shares="all" aria-pressed="true">All</button>
        <button type="button" class="year-chip" data-calls-shares="available" aria-pressed="false">Available</button>
      </div>`;
    host.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
        "[data-calls-shares]",
      );
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      callsSharesFilter = button.dataset.callsShares === "available" ? "available" : "all";
      for (const chip of host!.querySelectorAll<HTMLButtonElement>("[data-calls-shares]")) {
        const active = chip.dataset.callsShares === callsSharesFilter;
        chip.classList.toggle("active", active);
        chip.setAttribute("aria-pressed", String(active));
      }
      scheduleSync();
    });
  }

  const row = findCallsFilterRow(table);
  const tickerField = document.getElementById("wheelCallTicker")?.closest(".filter-field");
  if (row && tickerField) {
    if (host.parentElement !== row || host.previousElementSibling !== tickerField) {
      tickerField.insertAdjacentElement("afterend", host);
    }
  }

  for (const button of host.querySelectorAll<HTMLButtonElement>("[data-calls-shares]")) {
    const active = button.dataset.callsShares === callsSharesFilter;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  }
}

/** Hide Earnings / Cap / Sector / Rubric band on the Calls screener. */
function hideCallsScreenerFilters(): void {
  const root = document.querySelector<HTMLElement>('[data-wheel-view="calls"]');
  if (!root) return;

  for (const id of CALLS_FILTER_CONTROL_IDS) hideCallsFilterField(id);
  document.getElementById("sfe-rubric-filter-host")?.classList.add(CALLS_FILTER_HIDDEN);
  resetCallsHiddenFilters(root);
}

function hidePutsEarningsFilter(): void {
  const table = findRecsTable("put");
  const root =
    table?.closest<HTMLElement>('[data-wheel-view="puts"]') ??
    table?.parentElement?.parentElement;
  if (!root) return;

  for (const label of root.querySelectorAll("label")) {
    if (!(label instanceof HTMLLabelElement)) continue;
    const ownText = Array.from(label.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent ?? "")
      .join(" ")
      .trim();
    const visibleLabel = ownText || label.querySelector("span")?.textContent?.trim() || "";
    if (!/^earnings?$/i.test(visibleLabel)) continue;

    const parent = label.parentElement;
    const controls = parent?.querySelectorAll("select, input, button") ?? [];
    const host =
      parent && controls.length === 1 && (parent.textContent?.trim().length ?? 0) < 100
        ? parent
        : label;
    host.classList.add("sfe-puts-earnings-filter-hidden");

    if (host === label && label.htmlFor) {
      document.getElementById(label.htmlFor)?.classList.add("sfe-puts-earnings-filter-hidden");
    }
  }
}

function findRecsTable(side: "put" | "call"): HTMLTableElement | null {
  const id = side === "put" ? "wheelPutsTable" : "wheelCallsTable";
  const table = document.getElementById(id);
  return table instanceof HTMLTableElement ? table : null;
}

function findAnchorHeader(table: HTMLTableElement): HTMLTableCellElement | null {
  for (const col of ANCHOR_COLS) {
    const th = table.querySelector(`th[data-col="${col}"]`);
    if (th instanceof HTMLTableCellElement) return th;
  }
  return null;
}

function findAnchorCell(row: HTMLTableRowElement): HTMLTableCellElement | null {
  for (const col of ANCHOR_COLS) {
    const td = row.querySelector(`td[data-col="${col}"]`);
    if (td instanceof HTMLTableCellElement) return td;
  }
  return null;
}

function findEarningsAnchorHeader(table: HTMLTableElement): HTMLTableCellElement | null {
  for (const col of EARNINGS_ANCHOR_COLS) {
    const th = table.querySelector(`th[data-col="${col}"]`);
    if (th instanceof HTMLTableCellElement) return th;
  }
  for (const th of table.querySelectorAll("thead th")) {
    if (!(th instanceof HTMLTableCellElement)) continue;
    if (th.classList.contains(EARNINGS_HEADER) || th.classList.contains(PROFIT_HEADER)) continue;
    const label = (th.textContent ?? "").trim();
    if (/^expir(y|ation)$/i.test(label) || /^dte$/i.test(label)) return th;
  }
  return null;
}

function findEarningsAnchorCell(row: HTMLTableRowElement): HTMLTableCellElement | null {
  for (const col of EARNINGS_ANCHOR_COLS) {
    const td = row.querySelector(`td[data-col="${col}"]`);
    if (td instanceof HTMLTableCellElement) return td;
  }
  return null;
}

function normalizeDateKey(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const mdy = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (mdy) {
    const y = mdy[3].length === 2 ? `20${mdy[3]}` : mdy[3];
    return `${y}-${mdy[1].padStart(2, "0")}-${mdy[2].padStart(2, "0")}`;
  }
  const parsed = Date.parse(s);
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  return s.toUpperCase();
}

function recLookupKey(ticker: string, strike: number, expiration: string): string {
  return `${normalizeTicker(ticker)}|${strike}|${normalizeDateKey(expiration)}`;
}

function buildEarningsLookup(
  recs: WheelRecommendation[],
): Map<string, WheelRecommendation> {
  const map = new Map<string, WheelRecommendation>();
  for (const rec of recs) {
    const ticker = normalizeTicker(rec.underlying_ticker);
    if (!ticker) continue;
    if (!map.has(`tk:${ticker}`) && rec.next_earnings_date) {
      map.set(`tk:${ticker}`, rec);
    }
    if (Number.isFinite(rec.strike) && rec.expiration_date) {
      map.set(recLookupKey(ticker, rec.strike, rec.expiration_date), rec);
    }
    if (rec.option_symbol) map.set(`sym:${rec.option_symbol}`, rec);
  }
  return map;
}

function parseRowExpiration(row: HTMLTableRowElement): string {
  const cell = row.querySelector("td[data-col='expiration_date']");
  const fromAttr = cell?.getAttribute("data-expiration-date")?.trim();
  if (fromAttr) return fromAttr;
  return (
    cell?.textContent ??
    row.querySelector("td[data-col='expiration']")?.textContent ??
    row.querySelector("td[data-col='exp_date']")?.textContent ??
    ""
  ).trim();
}

function parseRowStrike(row: HTMLTableRowElement): number | undefined {
  return parseUsd(row.querySelector("td[data-col='strike']")?.textContent);
}

function matchRowRecommendation(
  row: HTMLTableRowElement,
  lookup: Map<string, WheelRecommendation>,
): WheelRecommendation | undefined {
  const sym =
    row.getAttribute("data-option-symbol") ||
    row.getAttribute("data-symbol") ||
    row.dataset.optionSymbol ||
    row.dataset.symbol;
  if (sym) {
    const bySym = lookup.get(`sym:${sym}`);
    if (bySym) return bySym;
  }

  const ticker = parseRowTicker(row);
  const strike = parseRowStrike(row);
  const expiration = parseRowExpiration(row);
  if (ticker && strike != null && expiration) {
    const exact = lookup.get(recLookupKey(ticker, strike, expiration));
    if (exact) return exact;
  }
  if (ticker) return lookup.get(`tk:${normalizeTicker(ticker)}`);
  return undefined;
}

function relayoutNativeRecsTable(table: HTMLTableElement): void {
  const side = tableSide(table);
  try {
    const api = (
      window as unknown as {
        WfaWheelRt?: {
          get?: (name: string) => { relayoutRecsFloat?: (s: "put" | "call") => void } | undefined;
        };
      }
    ).WfaWheelRt?.get?.("recommendations");
    if (api?.relayoutRecsFloat) {
      api.relayoutRecsFloat(side);
      return;
    }
  } catch {
    /* ignore */
  }

  table.querySelector("colgroup[data-recs-cols]")?.remove();
  table.style.tableLayout = "";
  table.style.width = "";
  const headCount = table.querySelector("thead tr")?.children.length ?? 0;
  if (headCount) table.style.minWidth = `${Math.max(720, headCount * 82)}px`;
}

export function parseScreenerRowTicker(row: HTMLTableRowElement): string {
  const cell = row.querySelector("td[data-col='underlying_ticker']");
  if (!cell) return "";
  const link = cell.querySelector(".recs-ticker, a");
  const text = (link?.textContent ?? cell.textContent ?? "").trim();
  const match = text.match(/^([A-Z]{1,6})\b/);
  return match ? match[1] : (text.split(/\s+/)[0]?.toUpperCase() ?? "");
}

function parseRowTicker(row: HTMLTableRowElement): string {
  return parseScreenerRowTicker(row);
}

export function getScreenerTableSide(table: HTMLTableElement): "put" | "call" {
  return tableSide(table);
}

function tableSide(table: HTMLTableElement): "put" | "call" {
  if (table.id === "wheelCallsTable") return "call";
  const row = table.querySelector("tbody tr[data-side]");
  const side = row?.getAttribute("data-side");
  return isCallSide(side ?? undefined) ? "call" : "put";
}

function ensureProfitCell(
  row: HTMLTableRowElement,
  anchor: HTMLTableCellElement,
): HTMLTableCellElement {
  let cell = row.querySelector<HTMLTableCellElement>(`.${PROFIT_CELL}`);
  if (!cell) {
    cell = document.createElement("td");
    cell.className = `num ${PROFIT_CELL}`;
    cell.setAttribute("data-col", PROFIT_COL);
    anchor.insertAdjacentElement("afterend", cell);
  } else if (cell.previousElementSibling !== anchor) {
    anchor.insertAdjacentElement("afterend", cell);
  }
  return cell;
}

function paintProfitCell(
  cell: HTMLTableCellElement,
  side: "put" | "call",
  stockAvgs: Map<string, number>,
  row: HTMLTableRowElement,
): number | undefined {
  const last = parseUsd(row.querySelector("td[data-col='last_price']")?.textContent);

  let result;
  if (side === "call") {
    const ticker = parseRowTicker(row);
    const avg =
      stockAvgs.get(normalizeTicker(ticker)) ??
      parseUsd(row.querySelector("td[data-col='holding_avg_cost']")?.textContent);
    result = callProfitability(last, avg);
  } else {
    const collateral = parseUsd(row.querySelector("td[data-col='collateral']")?.textContent);
    result = putProfitability(last, collateral);
  }

  cell.textContent = fmtProfitPct(result.pct);
  cell.setAttribute("data-tip", result.tip);
  cell.title = result.tip;
  cell.dataset.sfeProfitPct =
    result.pct != null && Number.isFinite(result.pct) ? String(result.pct) : "";
  return result.pct;
}

function ensureEarningsCell(
  row: HTMLTableRowElement,
  anchor: HTMLTableCellElement,
): HTMLTableCellElement {
  let cell = row.querySelector<HTMLTableCellElement>(`.${EARNINGS_CELL}`);
  if (!cell) {
    cell = document.createElement("td");
    cell.className = EARNINGS_CELL;
    cell.setAttribute("data-col", EARNINGS_COL);
    anchor.insertAdjacentElement("afterend", cell);
  } else if (cell.previousElementSibling !== anchor) {
    anchor.insertAdjacentElement("afterend", cell);
  }
  return cell;
}

function ensureEarningsHeader(table: HTMLTableElement): HTMLTableCellElement | null {
  const existing = table.querySelector(`.${EARNINGS_HEADER}`);
  if (existing instanceof HTMLTableCellElement) {
    const anchor = findEarningsAnchorHeader(table);
    if (anchor && existing.previousElementSibling !== anchor) {
      anchor.insertAdjacentElement("afterend", existing);
    }
    return existing;
  }

  const native = table.querySelector(
    `th[data-col="${EARNINGS_COL}"]:not(.${EARNINGS_HEADER})`,
  );
  if (native instanceof HTMLTableCellElement) return native;

  const anchor = findEarningsAnchorHeader(table);
  if (!anchor) return null;

  const th = document.createElement("th");
  th.className = EARNINGS_HEADER;
  th.setAttribute("data-col", EARNINGS_COL);
  th.innerHTML = "<span class='th-inner'><span class='th-label'>Earnings</span></span>";
  th.setAttribute("data-tip", "Next earnings date for the underlying (via Finviz).");
  anchor.insertAdjacentElement("afterend", th);
  return th;
}

function paintEarningsCell(
  cell: HTMLTableCellElement,
  rec: WheelRecommendation | undefined,
): void {
  const date = fmtEarningsDate(rec?.next_earnings_date);
  const tip = earningsDateTip(rec);
  const within = earningsWithinExpiry(rec);
  cell.setAttribute("data-tip", tip);
  cell.title = tip;
  cell.classList.remove("sfe-earnings-after");
  if (within) {
    cell.setAttribute("data-earnings-within-expiry", "true");
  } else {
    cell.removeAttribute("data-earnings-within-expiry");
  }

  if (date === "—") {
    cell.textContent = "—";
    return;
  }

  const inDays = fmtEarningsInDays(rec?.next_earnings_date);
  const subCls = within ? "sfe-earnings-sub sfe-earnings-within-expiry" : "sfe-earnings-sub";
  cell.innerHTML = `${date}<small class="${subCls}">${inDays}</small>`;
}

async function syncTable(
  table: HTMLTableElement,
  earningsLookup: Map<string, WheelRecommendation>,
): Promise<void> {
  const earningsHeader = ensureEarningsHeader(table);
  let needsRelayout = Boolean(earningsHeader);

  const anchorHeader = findAnchorHeader(table);
  if (anchorHeader) {
    let profitHeader = table.querySelector(`.${PROFIT_HEADER}`);
    if (!profitHeader) {
      const th = document.createElement("th");
      th.className = `num ${PROFIT_HEADER}`;
      th.setAttribute("data-col", PROFIT_COL);
      th.textContent = "Profit.";
      const side = tableSide(table);
      th.setAttribute(
        "data-tip",
        side === "call"
          ? "Premium as % of your average share cost: last option price ÷ avg cost."
          : "Premium as % of cash collateral: (last option price × 100) ÷ collateral.",
      );
      anchorHeader.insertAdjacentElement("afterend", th);
      profitHeader = th;
      needsRelayout = true;
    } else if (profitHeader.previousElementSibling !== anchorHeader) {
      anchorHeader.insertAdjacentElement("afterend", profitHeader);
      needsRelayout = true;
    }
  }

  const side = tableSide(table);
  if (side === "put") ensureMinProfitFilter(table);
  if (side === "call") ensureCallsSharesFilter(table);
  if (side === "call") ensureCallsRiskyFilters(table);
  if (side === "put") ensureSharesHeaderLabel(table, "held");
  if (side === "call") ensureSharesHeaderLabel(table, "open");
  const stockAvgs = side === "call" ? await loadOwnLedgerStrikeAvgMap(false) : new Map();

  let positionsSnapshot: PositionsSnapshot | null = null;
  if (side === "put" || side === "call") {
    try {
      positionsSnapshot = await loadPositionsSnapshot(false);
    } catch {
      positionsSnapshot = null;
    }
  }

  for (const tr of table.querySelectorAll("tbody tr")) {
    if (!(tr instanceof HTMLTableRowElement)) continue;

    const rec = matchRowRecommendation(tr, earningsLookup);
    const ticker = parseRowTicker(tr);
    const shares =
      positionsSnapshot && ticker ? sharesForTicker(positionsSnapshot, ticker) : null;
    if (rec && shares) {
      rec.sfe_shares_held = shares.held;
      rec.sfe_shares_available = shares.available;
    }
    if (side === "call") {
      applyCallsSharesFilter(tr, rec, shares);
      const avg =
        (ticker ? stockAvgs.get(normalizeTicker(ticker)) : undefined) ??
        rec?.holding_avg_cost ??
        undefined;
      if (ticker) {
        const avgCell = tr.querySelector<HTMLTableCellElement>("td[data-col='holding_avg_cost']");
        const resolved = stockAvgs.get(normalizeTicker(ticker));
        if (avgCell && resolved != null && Number.isFinite(resolved) && resolved > 0) {
          avgCell.textContent = fmtUsd(resolved);
        }
      }
      applyCallsRiskyRowFilters(tr, rec, avg ?? null);
    }
    if (side === "put" || side === "call") {
      const sharesCol = side === "put" ? "held" : "open";
      const sharesCell = tr.querySelector<HTMLTableCellElement>(`td[data-col='${sharesCol}']`);
      if (sharesCell) paintScreenerSharesCell(sharesCell, shares?.held, shares?.available);
    }
    const nativeEarningsCell = tr.querySelector<HTMLTableCellElement>(
      `td[data-col="${EARNINGS_COL}"]:not(.${EARNINGS_CELL})`,
    );
    if (nativeEarningsCell) {
      paintEarningsCell(nativeEarningsCell, rec);
    } else {
      const earningsAnchor = findEarningsAnchorCell(tr);
      if (earningsAnchor) {
        const before = earningsAnchor.nextElementSibling;
        const earningsCell = ensureEarningsCell(tr, earningsAnchor);
        if (before !== earningsCell) needsRelayout = true;
        paintEarningsCell(earningsCell, rec);
      }
    }

    if (!isWebCompactMode()) {
      const expiryCell = tr.querySelector<HTMLTableCellElement>("td[data-col='expiration_date']");
      if (expiryCell) paintScreenerExpiryCell(expiryCell, rec);
    }

    if (!anchorHeader) continue;
    const anchor = findAnchorCell(tr);
    if (!anchor) continue;
    const cell = ensureProfitCell(tr, anchor);
    const profitPct = paintProfitCell(cell, side, stockAvgs, tr);
    tr.classList.toggle(
      MIN_PROFIT_HIDDEN,
      side === "put" &&
        minPutProfit != null &&
        (profitPct == null || profitPct < minPutProfit),
    );
  }

  if (needsRelayout) {
    relayoutNativeRecsTable(table);
  }
}

export async function syncScreenerProfitColumns(): Promise<void> {
  if (applying) return;
  applying = true;
  try {
    hidePutsEarningsFilter();
    hideCallsScreenerFilters();
    const tables = [findRecsTable("put"), findRecsTable("call")].filter(
      (t): t is HTMLTableElement => t != null,
    );
    if (!tables.length) return;

    // Inject column structure immediately so fixed colgroup layout can be relaid out,
    // then fill dates once recommendation data is available.
    await Promise.all(tables.map((table) => syncTable(table, new Map())));

    let putLookup = new Map<string, WheelRecommendation>();
    let callLookup = new Map<string, WheelRecommendation>();
    try {
      const snapshot = await loadRecommendationsSnapshot(false);
      putLookup = buildEarningsLookup(snapshot.puts);
      callLookup = buildEarningsLookup(snapshot.calls);
    } catch {
      // Column still shows "—" if the fetch fails.
    }

    if (putLookup.size || callLookup.size) {
      await Promise.all(
        tables.map((table) =>
          syncTable(table, tableSide(table) === "call" ? callLookup : putLookup),
        ),
      );
    }
  } finally {
    applying = false;
    syncResetButtons();
  }
}

function scheduleSync(): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void syncScreenerProfitColumns();
  }, 100);
}

export function mountScreenerProfitColumn(): void {
  void syncScreenerProfitColumns();

  domObserver?.disconnect();
  domObserver = new MutationObserver((mutations) => {
    if (applying) return;
    hidePutsEarningsFilter();
    hideCallsScreenerFilters();
    const putsTable = findRecsTable("put");
    if (putsTable) ensureMinProfitFilter(putsTable);
    const touches = mutations.some((m) => {
      if (m.target instanceof Node) {
        const puts = findRecsTable("put");
        const calls = findRecsTable("call");
        if (puts?.contains(m.target) || calls?.contains(m.target)) return true;
      }
      for (const node of m.addedNodes) {
        if (node instanceof HTMLElement && (node.id === "wheelPutsTable" || node.id === "wheelCallsTable")) {
          return true;
        }
      }
      return false;
    });
    if (touches) scheduleSync();
  });
  domObserver.observe(document.body, { childList: true, subtree: true });
}
